/* eslint-disable react/no-unescaped-entities */
/**
 * /hustleup/join — 4-step signup wizard.
 *
 *   1. Identity  — first name, last name, email, mobile
 *   2. Verify    — 6-digit codes to email + mobile (SMS code stub-leaked
 *                  in dev so QA can paste it back)
 *   3. About you — postcode + AU declaration checkbox + 3+ interests
 *   4. Welcome   — store session token to localStorage, redirect to /me
 */
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  Users, ArrowLeft, ArrowRight, Mail, Phone, MapPin, CheckCircle2,
  Loader2, ShieldCheck, KeyRound,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
const api = axios.create({ baseURL: API });

const HU_TOKEN_KEY = "hu_session_token";

export default function HustleUpJoinPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const seedInterest = params.get("interest");

  const [step, setStep] = useState(1);
  const [pendingId, setPendingId] = useState(null);
  const [debugSmsCode, setDebugSmsCode] = useState(null);
  const [emailMasked, setEmailMasked] = useState("");
  const [mobileMasked, setMobileMasked] = useState("");

  // step 1
  const [firstName, setFirstName] = useState("");
  const [lastName,  setLastName]  = useState("");
  const [email,     setEmail]     = useState("");
  const [mobile,    setMobile]    = useState("");

  // step 2
  const [emailCode, setEmailCode] = useState("");
  const [phoneCode, setPhoneCode] = useState("");
  const [emailVerified, setEmailVerified] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);

  // step 3
  const [postcode, setPostcode] = useState("");
  const [inAU, setInAU] = useState(false);
  const [terms, setTerms] = useState(false);
  const [cats, setCats] = useState([]);
  const [picks, setPicks] = useState(seedInterest ? [seedInterest] : []);

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [inviteInfo, setInviteInfo] = useState(null);
  const inviteToken = params.get("ref");

  useEffect(() => {
    api.get("/api/hustleup/categories").then(r => setCats(r.data || []));
    if (inviteToken) {
      // Resolve invite + fire-and-forget click tracking
      api.get(`/api/hustleup/invites/${inviteToken}`)
        .then(r => setInviteInfo(r.data))
        .catch(() => {});
      api.post(`/api/hustleup/invites/${inviteToken}/click`).catch(() => {});
    }
  }, [inviteToken]);

  const togglePick = (slug) => {
    setPicks(p => p.includes(slug) ? p.filter(x => x !== slug) : [...p, slug]);
  };

  // ── step 1 → start signup ─────────────────────────────────────
  const startSignup = async (e) => {
    e?.preventDefault();
    setBusy(true); setErr("");
    try {
      const r = await api.post(`/api/hustleup/signup/start`, {
        email: email.trim(), mobile: mobile.trim(),
        first_name: firstName.trim(), last_name: lastName.trim() || null,
      });
      setPendingId(r.data.pending_id);
      setEmailMasked(r.data.email_masked);
      setMobileMasked(r.data.mobile_masked);
      if (r.data.debug_sms_code) setDebugSmsCode(r.data.debug_sms_code);
      setStep(2);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || e2.message);
    } finally { setBusy(false); }
  };

  const verifyEmail = async () => {
    setBusy(true); setErr("");
    try {
      await api.post(`/api/hustleup/signup/verify-email`,
        { pending_id: pendingId, code: emailCode.trim() });
      setEmailVerified(true);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };
  const verifyPhone = async () => {
    setBusy(true); setErr("");
    try {
      await api.post(`/api/hustleup/signup/verify-phone`,
        { pending_id: pendingId, code: phoneCode.trim() });
      setPhoneVerified(true);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const resend = async (channel) => {
    setErr("");
    try {
      const r = await api.post(
        `/api/hustleup/signup/resend-code?pending_id=${pendingId}&channel=${channel}`);
      if (r.data.debug_sms_code) setDebugSmsCode(r.data.debug_sms_code);
      alert(`A new ${channel === "email" ? "email" : "SMS"} code has been sent.`);
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
  };

  // ── step 3 → complete ─────────────────────────────────────────
  const complete = async () => {
    setBusy(true); setErr("");
    try {
      const r = await api.post(`/api/hustleup/signup/complete`, {
        pending_id: pendingId,
        postcode: postcode.trim(),
        is_in_australia: inAU,
        accepts_terms: terms,
        interests: picks,
        invite_token: inviteToken || null,
      });
      localStorage.setItem(HU_TOKEN_KEY, r.data.session_token);
      setStep(4);
      setTimeout(() => navigate("/hustleup/me"), 2200);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div style={s.root} data-testid="hu-join-root">
      <header style={s.header}>
        <a href="/hustleup" style={s.back} data-testid="hu-back">
          <ArrowLeft size={14} /> HustleUp
        </a>
        <div style={s.brand}>
          <Users size={20} />
          <span>Hustle<span style={{ color: "#fbbf24" }}>Up</span>
            <small style={{ color: "#94a3b8", marginLeft: 4 }}>.au</small></span>
        </div>
      </header>

      <main style={s.main}>
        {inviteInfo && (
          <div style={{ background: "#fef3c7", border: "1px solid #fde68a",
                        borderRadius: 12, padding: 14, marginBottom: 18,
                        display: "flex", alignItems: "flex-start", gap: 12 }}
               data-testid="hu-invite-banner">
            <ShieldCheck size={20} color="#92400e" />
            <div style={{ fontSize: 13, color: "#92400e", lineHeight: 1.5 }}>
              <strong>Invited by {inviteInfo.organiser_name}</strong> from{" "}
              <em>{inviteInfo.source_group_name}</em>. Your free membership
              is waiting — finish in under a minute.
            </div>
          </div>
        )}
        <Stepper step={step} />

        {step === 1 && (
          <form style={s.card} onSubmit={startSignup} data-testid="hu-step-identity">
            <h2 style={s.h2}>G'day — let's get you in</h2>
            <p style={s.sub}>
              Free membership. We'll send a quick code to your email and
              your mobile to confirm you're a real human.
            </p>
            <div style={s.row2}>
              <Field label="First name">
                <input required value={firstName}
                       onChange={e => setFirstName(e.target.value)}
                       style={s.input} data-testid="hu-fn" />
              </Field>
              <Field label="Last name (optional)">
                <input value={lastName} onChange={e => setLastName(e.target.value)}
                       style={s.input} data-testid="hu-ln" />
              </Field>
            </div>
            <Field label="Email">
              <input required type="email" value={email}
                     onChange={e => setEmail(e.target.value)}
                     style={s.input} data-testid="hu-email" />
            </Field>
            <Field label="Mobile (Australian format works best)">
              <input required type="tel" placeholder="0412 345 678"
                     value={mobile} onChange={e => setMobile(e.target.value)}
                     style={s.input} data-testid="hu-mobile" />
            </Field>
            {err && <div style={s.err}>{err}</div>}
            <button type="submit" disabled={busy}
                    style={{ ...s.btn, opacity: busy ? 0.5 : 1 }}
                    data-testid="hu-step1-submit">
              {busy
                ? <><Loader2 size={16} className="spin" /> Sending codes…</>
                : <>Send my codes <ArrowRight size={16} /></>}
            </button>
          </form>
        )}

        {step === 2 && (
          <div style={s.card} data-testid="hu-step-verify">
            <h2 style={s.h2}>Two quick codes</h2>
            <p style={s.sub}>
              We sent a 6-digit code to <strong>{emailMasked}</strong>
              {" "}and another to <strong>{mobileMasked}</strong>.
              Type each in to verify.
            </p>

            {debugSmsCode && (
              <div style={s.stubBanner} data-testid="hu-debug-sms-code">
                <KeyRound size={14} />
                <span>
                  Demo mode — your SMS code is{" "}
                  <code style={{ background: "#fbbf24", color: "#0f172a",
                                 padding: "2px 8px", borderRadius: 4,
                                 fontFamily: "monospace", fontSize: 13 }}>
                    {debugSmsCode}
                  </code>
                </span>
              </div>
            )}

            <CodeRow icon={Mail} label="Email code"
                     value={emailCode} onChange={setEmailCode}
                     onSubmit={verifyEmail} verified={emailVerified}
                     onResend={() => resend("email")} busy={busy}
                     testid="email" />
            <CodeRow icon={Phone} label="SMS code"
                     value={phoneCode} onChange={setPhoneCode}
                     onSubmit={verifyPhone} verified={phoneVerified}
                     onResend={() => resend("phone")} busy={busy}
                     testid="phone" />

            {err && <div style={s.err}>{err}</div>}
            <button disabled={!emailVerified || !phoneVerified}
                    style={{ ...s.btn,
                             opacity: (!emailVerified || !phoneVerified) ? 0.5 : 1 }}
                    onClick={() => setStep(3)} data-testid="hu-step2-next">
              Continue <ArrowRight size={16} />
            </button>
          </div>
        )}

        {step === 3 && (
          <div style={s.card} data-testid="hu-step-profile">
            <h2 style={s.h2}>About you</h2>
            <p style={s.sub}>
              We need a postcode (for nearby groups) and at least 3
              interests to wire up your feed.
            </p>
            <Field label="Postcode">
              <input required value={postcode} maxLength={4}
                     placeholder="e.g. 4101"
                     onChange={e => setPostcode(e.target.value)}
                     style={s.input} data-testid="hu-postcode" />
            </Field>
            <label style={s.checkbox} data-testid="hu-in-au-wrap">
              <input type="checkbox" checked={inAU}
                     onChange={e => setInAU(e.target.checked)}
                     data-testid="hu-in-au" />
              <span>
                I am Australian or currently living in Australia
                (including as a visitor or on a working / student visa).
              </span>
            </label>
            <label style={s.checkbox}>
              <input type="checkbox" checked={terms}
                     onChange={e => setTerms(e.target.checked)}
                     data-testid="hu-terms" />
              <span>
                I agree to the HustleUp community guidelines — be
                respectful, no spam, no commercial DMs without consent.
              </span>
            </label>

            <h3 style={s.h3}>Pick at least 3 interests</h3>
            <div style={s.picks} data-testid="hu-picks">
              {cats.map(c => (
                <button key={c.slug} type="button"
                        onClick={() => togglePick(c.slug)}
                        style={{
                          ...s.pick,
                          ...(picks.includes(c.slug) ? s.pickActive : {}),
                          borderColor: picks.includes(c.slug) ? c.color : "#e8e2d4",
                        }}
                        data-testid={`hu-pick-${c.slug}`}>
                  {c.name}
                </button>
              ))}
            </div>
            <div style={s.pickCount} data-testid="hu-pick-count">
              {picks.length} of 3+ selected
            </div>

            {err && <div style={s.err}>{err}</div>}
            <button onClick={complete}
                    disabled={busy || !inAU || !terms || picks.length < 3
                              || postcode.length !== 4}
                    style={{ ...s.btn,
                             opacity: (busy || !inAU || !terms
                                       || picks.length < 3
                                       || postcode.length !== 4) ? 0.5 : 1 }}
                    data-testid="hu-finish">
              {busy
                ? <><Loader2 size={16} className="spin" /> Creating account…</>
                : <>Finish & join <CheckCircle2 size={16} /></>}
            </button>
          </div>
        )}

        {step === 4 && (
          <div style={s.card} data-testid="hu-step-done">
            <div style={s.doneBadge}><CheckCircle2 size={44} /></div>
            <h2 style={s.h2}>Welcome to HustleUp 🎉</h2>
            <p style={s.sub}>
              Your free membership is live. Taking you to your profile…
            </p>
            <Loader2 size={20} className="spin" />
          </div>
        )}
      </main>

      <style>{spinCss}</style>
    </div>
  );
}

function Stepper({ step }) {
  const labels = ["Identity", "Verify", "About you", "Done"];
  return (
    <div style={s.stepper} data-testid="hu-stepper">
      {labels.map((l, i) => (
        <span key={l} style={{
          ...s.dot,
          ...(step === i + 1 ? s.dotActive : {}),
          ...(step > i + 1 ? s.dotDone : {}),
        }}>{i + 1}. {l}</span>
      ))}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label style={s.field}>
      <span style={s.fieldLabel}>{label}</span>
      {children}
    </label>
  );
}

function CodeRow({ icon: Icon, label, value, onChange, onSubmit, verified,
                    onResend, busy, testid }) {
  return (
    <div style={s.codeRow}>
      <div style={s.codeRowHead}>
        <Icon size={14} /> {label}
        {verified && <span style={s.verified}>✓ Verified</span>}
      </div>
      {!verified && (
        <div style={s.codeInputs}>
          <input
            inputMode="numeric" maxLength={6} value={value}
            onChange={e => onChange(e.target.value.replace(/\D/g, ""))}
            style={s.codeInput} placeholder="123456"
            data-testid={`hu-code-${testid}`}
          />
          <button onClick={onSubmit} disabled={busy || value.length < 4}
                  style={{ ...s.codeBtn,
                           opacity: (busy || value.length < 4) ? 0.5 : 1 }}
                  data-testid={`hu-verify-${testid}`}>
            Verify
          </button>
          <button onClick={onResend} type="button" style={s.resendBtn}
                  data-testid={`hu-resend-${testid}`}>
            Resend
          </button>
        </div>
      )}
    </div>
  );
}

const spinCss = `.spin{animation:spin 1s linear infinite}
                  @keyframes spin{to{transform:rotate(360deg)}}`;

const s = {
  root: { background: "#fffef5", minHeight: "100vh",
          fontFamily: "Inter, system-ui, sans-serif", color: "#0f172a" },
  header: { display: "flex", justifyContent: "space-between",
            alignItems: "center", padding: "18px 32px",
            background: "#0f172a", color: "#fbbf24" },
  back: { color: "#fbbf24", textDecoration: "none", fontSize: 13,
          display: "inline-flex", alignItems: "center", gap: 6,
          fontWeight: 500 },
  brand: { display: "inline-flex", alignItems: "center", gap: 8,
           fontWeight: 800, fontFamily: "Georgia, serif", fontSize: 20,
           color: "white" },
  main: { maxWidth: 640, margin: "0 auto", padding: "40px 20px 80px" },
  stepper: { display: "flex", gap: 8, flexWrap: "wrap",
             marginBottom: 28, justifyContent: "center" },
  dot: { background: "#fef3c7", color: "#92400e", padding: "5px 12px",
         borderRadius: 999, fontSize: 12, fontWeight: 600 },
  dotActive: { background: "#0f172a", color: "#fbbf24" },
  dotDone: { background: "#dcfce7", color: "#16a34a" },
  card: { background: "white", padding: 36, borderRadius: 18,
          boxShadow: "0 20px 60px rgba(0,0,0,0.06)" },
  h2: { fontFamily: "Georgia, serif", fontSize: 28, fontWeight: 800,
        letterSpacing: "-0.025em", margin: "0 0 8px" },
  h3: { fontSize: 13, fontWeight: 800, letterSpacing: "0.06em",
        textTransform: "uppercase", color: "#64748b",
        margin: "24px 0 12px" },
  sub: { color: "#475569", margin: "0 0 24px", lineHeight: 1.55,
         fontSize: 14 },
  field: { display: "flex", flexDirection: "column", gap: 4,
           marginBottom: 14 },
  row2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 },
  fieldLabel: { fontSize: 11, fontWeight: 700, color: "#64748b",
                textTransform: "uppercase", letterSpacing: "0.06em" },
  input: { padding: "12px 14px", border: "1px solid #e8e2d4",
           borderRadius: 10, fontSize: 15, outline: "none",
           fontFamily: "inherit" },
  checkbox: { display: "flex", alignItems: "start", gap: 10,
              marginTop: 12, padding: 14, background: "#fef3c7",
              border: "1px solid #fde68a", borderRadius: 10,
              fontSize: 13, lineHeight: 1.5, cursor: "pointer" },
  btn: { display: "inline-flex", alignItems: "center", justifyContent: "center",
         gap: 8, marginTop: 20, padding: "14px 22px", background: "#0f172a",
         color: "#fbbf24", border: "none", borderRadius: 12,
         fontWeight: 700, fontSize: 15, cursor: "pointer", width: "100%" },
  err: { background: "#fee2e2", color: "#991b1b", padding: "10px 14px",
         borderRadius: 8, fontSize: 13, marginTop: 12 },
  stubBanner: { display: "flex", alignItems: "center", gap: 8,
                background: "#0f172a", color: "white",
                padding: "12px 14px", borderRadius: 10,
                fontSize: 12, marginBottom: 18 },
  codeRow: { background: "#fffbeb", border: "1px solid #fde68a",
             borderRadius: 12, padding: 16, marginBottom: 14 },
  codeRowHead: { display: "flex", alignItems: "center", gap: 6,
                 fontSize: 12, fontWeight: 700, color: "#92400e",
                 textTransform: "uppercase", letterSpacing: "0.08em",
                 marginBottom: 10 },
  verified: { marginLeft: "auto", color: "#16a34a", fontSize: 12,
              fontWeight: 700 },
  codeInputs: { display: "flex", gap: 8 },
  codeInput: { flex: 1, padding: "12px 14px", border: "1px solid #fde68a",
               borderRadius: 8, fontSize: 18, outline: "none",
               fontFamily: "monospace", letterSpacing: "0.2em",
               textAlign: "center" },
  codeBtn: { background: "#0f172a", color: "#fbbf24", border: "none",
             padding: "0 18px", borderRadius: 8, fontWeight: 700,
             cursor: "pointer", fontSize: 13 },
  resendBtn: { background: "transparent", border: "1px solid #fde68a",
               color: "#92400e", padding: "0 14px", borderRadius: 8,
               cursor: "pointer", fontSize: 12, fontWeight: 600 },
  picks: { display: "flex", flexWrap: "wrap", gap: 8 },
  pick: { background: "white", border: "1px solid #e8e2d4",
          padding: "6px 14px", borderRadius: 999, fontSize: 13,
          fontWeight: 600, cursor: "pointer", color: "#0f172a" },
  pickActive: { background: "#0f172a", color: "#fbbf24",
                borderColor: "#0f172a" },
  pickCount: { fontSize: 12, color: "#64748b", marginTop: 10,
               fontWeight: 600 },
  doneBadge: { width: 76, height: 76, borderRadius: "50%",
               background: "#fef3c7", color: "#f59e0b",
               display: "inline-flex", alignItems: "center",
               justifyContent: "center", marginBottom: 16 },
};
